// -----------------------------------------------------------------------------
// Purpose:
// - Backoffice workflow page for reviewing a Tender while it is "In Quotation".
// - Loads Tender data, phases (for non-spot), carriers, lanes, and submitted quotes.
// - Supports FOUR inline views:
//      1) In-Quotation overview (form + carrier responses + quote analysis)
//      2) Carrier detail (per carrier/per phase breakdown)
//      3) Lane view (accepted quotes grouped by lane)   [NOW uses ALL lanes]
//      4) Summary view (lane aggregated status, reconciled with current accepted quotes) [NOW uses ALL lanes]
// - Persists all UI state + loaded datasets per-tab (namespaced snapshot) so users
//   can switch tabs without losing context or refetching everything.
// -----------------------------------------------------------------------------

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  MHeader,
  MButton,
  BufferService,
  MMessageBoard,
  M_StateActions,
  NavigateContext,
  MSelect,
} from "src/M_Components";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

// Presentational children
import InQuotationForm from "../../components/Tenders/InQuotationForm";
import CarrierResponsesTable from "../../components/Tenders/carrierResponsesTable";
import TenderQuotesAnalysis from "../../components/Tenders/TenderQuoteAnalysis";
import CarrierResponseDetailTable from "../../components/Tenders/CarrierResponseDetailTable";
import ViewLaneTable from "../../components/Tenders/ViewLaneTable";
import TenderSummaryTable from "../../components/Tenders/TenderSummaryTable";

import "./TenderInQuotationPage.css";

// ===================================================================================================
// Helpers (pure + tiny)
// ===================================================================================================
const stateKeyFrom = (tid) => (tid ? `${tid}::TIQ` : null);
const pickKV = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

// Normalizes common API envelope shapes
const norm = (data) =>
  Array.isArray(data?.Items)
    ? data.Items
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.Data)
        ? data.Data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : data || [];

const deepEq = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

// Page sizes for server-side pagination
const QUOTES_PAGE_SIZE = 200;
const LANES_PAGE_SIZE_FLAT = 200;
const SUMMARY_PAGE_SIZE = 200; //(aggregated /TenderLanes endpoint)
const ALL_LANES_PAGE_SIZE = 200;
// Distinct carriers can be in the hundreds/thousands; always page server-side
const DISTINCT_CARRIERS_PAGE_SIZE = 200;

// Quote field helpers for mixed casing payloads
const getQuotePhaseId = (q) => q?.TenderPhaseId ?? q?.tenderPhaseId;
const getQuoteId = (q) => q?.QuoteId ?? q?.quoteId ?? q?.Id ?? q?.id ?? null;

// Lane helpers for mixed casing payloads
const getLaneId = (lane) =>
  lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;
const getLaneOrigin = (lane) =>
  lane?.LaneSource ??
  lane?.laneSource ??
  lane?.Origin ??
  lane?.origin ??
  lane?.sourceLocation ??
  lane?.SourceLocation ??
  "";
const getLaneDestination = (lane) =>
  lane?.LaneDestination ??
  lane?.laneDestination ??
  lane?.Destination ??
  lane?.destination ??
  lane?.destinationLocation ??
  lane?.DestinationLocation ??
  "";

// Summary helpers (API row shape can vary in casing)
const getSummarySatisfiedPhaseId = (r) =>
  r?.satisfiedInTenderPhaseId ?? r?.SatisfiedInTenderPhaseId ?? null;
const getSummarySatisfiedAt = (r) => r?.satisfiedAt ?? r?.SatisfiedAt ?? null;

// Storage mirror (sessionStorage, per namespace)
const ssKey = (ns) => `NS_SNAPSHOT:${ns}`;
const readFromStorage = (ns) => {
  if (!ns) return null;
  try {
    const raw = sessionStorage.getItem(ssKey(ns));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
const writeToStorage = (ns, snapshotArray) => {
  if (!ns) return;
  try {
    sessionStorage.setItem(
      ssKey(ns),
      JSON.stringify(Array.isArray(snapshotArray) ? snapshotArray : [])
    );
  } catch {
    // Storage may be unavailable or full, so we keep Redux as the main source of truth.
  }
};

// ===================================================================================================
//  : last phase resolver + quote enrichment (pure)
// - We avoid relying on BE returning IsLastPhase on quotes.
// - We derive "last phase" deterministically from phases graph (LastTenderPhaseId chain).
// - We then FLATTEN IsLastPhase onto all quotes so ViewLaneTable can enforce toggle rules.
// ===================================================================================================
const resolveLastPhaseId = (phasesList) => {
  const list = Array.isArray(phasesList) ? phasesList : [];
  if (!list.length) return null;

  const lastIds = list
    .map((p) => p?.LastTenderPhaseId ?? p?.lastTenderPhaseId ?? null)
    .filter((x) => x != null);

  const last =
    list.find((p) => !lastIds.includes(p.Id)) || list[list.length - 1];

  return last?.Id ?? null;
};

const withIsLastPhase = (quotesList, lastPhaseId) => {
  const list = Array.isArray(quotesList) ? quotesList : [];
  if (!lastPhaseId) return list;

  return list.map((q) => {
    const qPhaseId = q?.TenderPhaseId ?? q?.tenderPhaseId ?? null;
    const isLast = qPhaseId != null && Number(qPhaseId) === Number(lastPhaseId);

    // Flattened flag (what ViewLaneTable reads)
    return {
      ...q,
      IsLastPhase: isLast,
      isLastPhase: isLast,
    };
  });
};

// ===================================================================================================
// Snapshot keys (persisted per tab namespace)
// ===================================================================================================
const K = {
  owner: "TIQ.ownerTabId",
  operation: "TIQ.operation",
  sessionLine: "TIQ.sessionLine",
  activeView: "TIQ.activeView",
  selectedCarrierId: "TIQ.selectedCarrierId",
  selectedPhaseId: "TIQ.selectedPhaseId",

  // Inline carrier detail specific UI state
  detailPhaseId: "TIQ.detailPhaseId",
  bestMetric: "TIQ.bestMetric", // 'bid' | 'loads' | 'etd'

  // Full datasets (to rehydrate instantly without hitting the network)
  tenderInfo: "TIQ.tenderInfo",
  phases: "TIQ.phases",
  quotes: "TIQ.quotes",

  // lanes = current lanes (from /flat)
  lanes: "TIQ.lanes",

  // allLanes = full tender lanes (from /TenderLanes)
  allLanes: "TIQ.allLanes",

  carriers: "TIQ.carriers",

  // Carriers-with-quotes (already filtered server-side via /distinctCarriers)
  distinctCarriersItems: "TIQ.distinctCarriersItems",

  // Summary dataset (persisted aggregates from /TenderLanes)
  summary: "TIQ.summary",

  // Lane summaries dataset (aggregated per lane) from /laneSummaries
  laneSummaries: "TIQ.laneSummaries",
  laneSummariesAccepted: "TIQ.laneSummariesAccepted",
  laneSummariesAcceptedCurrent: "TIQ.laneSummariesAcceptedCurrent",
  // Sync marker (prevents stale data after closing/reopening the page)
  lastSyncAt: "TIQ.lastSyncAt",

  // Tender options (schema + FKs) for TenderMainForm in View mode
  tenderSchemaOptions: "TIQ.tenderSchemaOptions",
  tenderFkData: "TIQ.tenderFkData",
};

// ===================================================================================================
// Component: TenderInQuotationPage
// ===================================================================================================
export default function TenderInQuotationPage({
  handleTabClose,
  toastMessages,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renameTab } = useContext(NavigateContext);

  // -------------------------------------------------------------------------------------------------
  // Redux picks (fine-grained; do not subscribe to the whole M_State array)
  // -------------------------------------------------------------------------------------------------
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );
  const tabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );
  const globalOperation = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "operation")?.value
  );
  const globalLine = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "TenderLine")?.value
  );

  const stateKeyValue = useSelector((s) => s.StateKeyValue);

  const allStateKeys = useMemo(
    () => (stateKeyValue || []).map((o) => o.key),
    [stateKeyValue]
  );


  // Current namespace snapshot (for the selected tab only)
  const currentNsKey = stateKeyFrom(selectedTab);
  const currentNsSnapshot = useSelector((s) =>
    currentNsKey
      ? s.StateKeyValue.find((o) => o.key === currentNsKey)?.value
      : undefined
  );

  // Keep a local mirror of the current tab id (compat)
  const [id, setId] = useState(selectedTab);
  useEffect(() => {
    setId(selectedTab);
  }, [selectedTab]);

  // -------------------------------------------------------------------------------------------------
  // Local tab state (persisted in the namespaced snapshot)
  // -------------------------------------------------------------------------------------------------
  const [operation, setOperation] = useState(globalOperation ?? "View");
  const [sessionLine, setSessionLine] = useState(globalLine ?? null);
  const [activeView, setActiveView] = useState("inQuotation"); // 'inQuotation' | 'carrier' | 'lane' | 'summary'

  const [selectedCarrierId, setSelectedCarrierId] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null); // in-quotation
  const [detailPhaseId, setDetailPhaseId] = useState(null); // carrier detail (non-spot)
  const [bestMetric, setBestMetric] = useState("bid"); // 'bid' | 'loads' | 'etd'
  const [isFirst, setIsFirst] = useState(true);

  // Timestamp from last successful sync with backend
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // -------------------------------------------------------------------------------------------------
  // Toast/loading helpers stored in refs (avoid ctx object dependency loops)
  // -------------------------------------------------------------------------------------------------
  const toastRef = useRef(toastMessages || ctx.toastMessages);
  const setLoadRef = useRef(ctx.setIsLoad);

  // Loading counter bound to ctx.setIsLoad (stable ref to avoid stale closures)
  const loadCountRef = useRef(0);

  useEffect(() => {
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  const incLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = prev + 1;
    loadCountRef.current = next;
    // Only flip the global loading flag when going from 0 -> 1
    if (prev === 0) setLoadRef.current?.(true);
  }, []);




  const decLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = Math.max(0, prev - 1);
    loadCountRef.current = next;
    // Only flip the global loading flag when going from 1 -> 0
    if (prev === 1 && next === 0) setLoadRef.current?.(false);
  }, []);

  const lastSyncAtRef = useRef(null);
  useEffect(() => { lastSyncAtRef.current = lastSyncAt; }, [lastSyncAt]);

  useEffect(() => {
    toastRef.current = toastMessages || ctx.toastMessages;
  }, [toastMessages, ctx.toastMessages]);

  useEffect(() => {
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  // -------------------------------------------------------------------------------------------------
  // Services (stable via baseUrl)
  // -------------------------------------------------------------------------------------------------
  const baseUrl = ctx.DEFAULTURL;

  const tenderService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderServiceUpdate = useMemo(
    () => new BufferService("Tenders/update", baseUrl),
    [baseUrl]
  );
  const phasesService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );


  //Lane summaries endpoint (aggregated per lane)
  const laneSummariesService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries", baseUrl),
    [baseUrl]
  );

  // Lane summaries for accepted quotes only
  const laneSummariesAcceptedService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries/accepted", baseUrl),
    [baseUrl]
  );

  // Lightweight endpoint that returns ONLY the carriers that have quotes (plus counts).
  // This replaces the FE-side filtering of carriers based on hvQuotesAll.
  const distinctCarriersService = useMemo(
    () => new BufferService("TenderQuotes/distinctCarriers", baseUrl),
    [baseUrl]
  );

  // Service used to accept / unaccept quotes by QuoteId
  const tenderQuotesUpdateService = useMemo(
    () => new BufferService("TenderQuotes/update", baseUrl),
    [baseUrl]
  );

  // Lanes are served by a unified endpoint for both Spot and Non-Spot.
  const tenderLanesFlatService = useMemo(
    () => new BufferService("TenderLanes/flat", baseUrl),
    [baseUrl]
  );

  // Summary endpoint for persisted lane aggregates
  const tenderLanesSummaryService = useMemo(
    () => new BufferService("TenderLanes", baseUrl),
    [baseUrl]
  );

  const phaseCarriersService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderOptionsService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );

  // -------------------------------------------------------------------------------------------------
  // Loaded datasets (persisted in snapshot so we can hydrate instantly)
  // -------------------------------------------------------------------------------------------------
  const [tenderInfo, setTenderInfo] = useState({});
  const [phases, setPhases] = useState([]);

  // Quotes are stored in three shapes:
  // - quotes: the full list (accepted + unaccepted) stored in the snapshot
  // - quotesAccepted: accepted subset used by the lane view
  // - quotesUnaccepted: unaccepted subset used only for completeness and future logic
  const [quotes, setQuotes] = useState([]);
  const [quotesAccepted, setQuotesAccepted] = useState([]);
  const [quotesUnaccepted, setQuotesUnaccepted] = useState([]);

  // lanes (current, from /flat)
  const [lanes, setLanes] = useState([]);

  // allLanes (global tender, from /TenderLanes)
  const [allLanes, setAllLanes] = useState([]);

  const [carriers, setCarriers] = useState([]);

  // Carriers that actually have quotes, already filtered on the backend.
  // Shape comes from /distinctCarriers (paged): { Carrier, QuotesCount, QuotesAcceptedCount }.
  const [distinctCarriersItems, setDistinctCarriersItems] = useState([]);

  // persisted aggregates from backend (/TenderLanes)
  const [summaryRows, setSummaryRows] = useState([]);


  // aggregated lane summaries (per lane) from /laneSummaries (phase-aware for Non-Spot)
  const [laneSummariesRows, setLaneSummariesRows] = useState([]);

  // aggregated lane summaries for accepted quotes only (from /laneSummaries/accepted)
  const [laneSummariesAcceptedRows, setLaneSummariesAcceptedRows] = useState([]);

  // aggregated lane summaries for accepted quotes in the CURRENT phase only
  //    (from /laneSummaries/accepted filtered by TenderPhaseId = lastPhaseId)
  const [
    laneSummariesAcceptedCurrentRows,
    setLaneSummariesAcceptedCurrentRows,
  ] = useState([]);


  const [refreshKey, setRefreshKey] = useState(0);
  // Forces reloading of phase-specific carrier lists (/distinctCarriers)
  const [distinctCarriersReloadKey, setDistinctCarriersReloadKey] = useState(0);

  const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);
  const [tenderFkData, setTenderFkData] = useState({});

  // Busy ids for accept/unaccept operations, shared across all tables
  const [acceptBusyIds, setAcceptBusyIds] = useState(() => new Set());
  const acceptBusyIdsRef = useRef(acceptBusyIds);

  // =================================================================================================
  // Quote field helpers (shape adapters)
  // - Some endpoints/DTOs return PascalCase (IsAccepted) while others return camelCase (isAccepted).
  // - This helper normalizes access so the rest of the page stays clean.
  // =================================================================================================
  const getQuoteAccepted = (q) => {
    if (!q) return false;
    return Boolean(q.IsAccepted ?? q.isAccepted ?? false);
  };

  useEffect(() => {
    acceptBusyIdsRef.current = acceptBusyIds;
  }, [acceptBusyIds]);

  // -------------------------------------------------------------------------------------------------
  // Namespaced snapshot infra
  // -------------------------------------------------------------------------------------------------
  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const persistDebounceRef = useRef(null);
  const didGCRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const nsSnapshotRef = useRef([]);
  const rehydratingRef = useRef(false);

  // Keep an up-to-date copy of the active namespace snapshot
  useEffect(() => {
    if (currentNsKey && activeNsRef.current === currentNsKey) {
      nsSnapshotRef.current = Array.isArray(currentNsSnapshot)
        ? currentNsSnapshot
        : [];
    }
  }, [currentNsKey, currentNsSnapshot]);

  // =================================================================================================
  // setSession(nsKey, entries)
  // =================================================================================================
  const setSession = useCallback(
    (nsKey, entries) => {
      if (!nsKey) return;

      const current =
        nsKey === activeNsRef.current ? nsSnapshotRef.current || [] : [];
      const map = new Map(current.map((e) => [e.key, e]));
      let changed = false;

      for (const kv of entries) {
        const prevVal = map.get(kv.key)?.value;
        if (!deepEq(prevVal, kv.value)) {
          changed = true;
          map.set(kv.key, kv);
        }
      }

      if (changed) {
        const nextArray = Array.from(map.values());
        // 1) Redux
        dispatch(M_StateActions.addM_State({ key: nsKey, value: nextArray }));
        // 2) sessionStorage mirror
        writeToStorage(nsKey, nextArray);
        // 3) local ref
        if (nsKey === activeNsRef.current) nsSnapshotRef.current = nextArray;
      }
    },
    [dispatch]
  );

  // =================================================================================================
  // flushSnapshot(forTabId?)
  // =================================================================================================
  const flushSnapshot = useCallback(
    (forTabId = null) => {
      const tid = forTabId ?? activeTidRef.current ?? id;
      const ns = stateKeyFrom(tid);
      if (!ns) return;

      const payload = [
        { key: K.owner, value: String(tid) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: tenderInfo || {} },
        { key: K.phases, value: phases || [] },
        { key: K.laneSummaries, value: laneSummariesRows || [] },
        { key: K.laneSummariesAccepted, value: laneSummariesAcceptedRows || [] },
        { key: K.laneSummariesAcceptedCurrent, value: laneSummariesAcceptedCurrentRows || [] },
        // The snapshot stores the full quotes list (accepted + unaccepted)
        { key: K.quotes, value: quotes || [] },

        // lanes (current, /flat)
        { key: K.lanes, value: lanes || [] },

        // allLanes (global, /TenderLanes)
        { key: K.allLanes, value: allLanes || [] },

        { key: K.carriers, value: carriers || [] },

        // Carriers-with-quotes (already filtered server-side via /distinctCarriers)
        { key: K.distinctCarriersItems, value: distinctCarriersItems || [] },

        // persisted aggregates
        { key: K.summary, value: summaryRows || [] },

        // sync marker
        { key: K.lastSyncAt, value: lastSyncAt },

        { key: K.tenderSchemaOptions, value: tenderSchemaOptions || [] },
        { key: K.tenderFkData, value: tenderFkData || {} },
      ];

      setSession(ns, payload);
    },
    [id, operation, sessionLine, activeView, selectedCarrierId, selectedPhaseId, detailPhaseId, bestMetric, tenderInfo, phases, laneSummariesRows, laneSummariesAcceptedRows, laneSummariesAcceptedCurrentRows, quotes, lanes, allLanes, carriers, distinctCarriersItems, summaryRows, lastSyncAt, tenderSchemaOptions, tenderFkData, setSession]
  );

  // =================================================================================================
  // GC: remove orphan TIQ namespaces (only once)
  // =================================================================================================
  useEffect(() => {
    if (didGCRef.current) return;
    if (!tabsList || tabsList.length === 0) return;
    didGCRef.current = true;

    try {
      const openIds = new Set((tabsList || []).map((t) => String(t.value)));
      (allStateKeys || [])
        .filter((k) => typeof k === "string" && k.endsWith("::TIQ"))
        .forEach((ns) => {
          const tid = ns.split("::")[0];
          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns }));
            sessionStorage.removeItem(ssKey(ns));
          }
        });
    } catch {
      /* noop */
    }
  }, [tabsList, allStateKeys, dispatch]);

  // =================================================================================================
  // HYDRATE (SYNC before paint)
  // =================================================================================================
  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== selectedTab) flushSnapshot(prev);

    if (!selectedTab) {
      prevTabRef.current = null;
      return;
    }

    const ns = stateKeyFrom(selectedTab);
    activeNsRef.current = ns;
    activeTidRef.current = selectedTab;

    let snap = Array.isArray(currentNsSnapshot) ? currentNsSnapshot : [];

    if (!snap.length) {
      const fromStore = readFromStorage(ns);
      if (Array.isArray(fromStore) && fromStore.length) {
        snap = fromStore;
        dispatch(M_StateActions.addM_State({ key: ns, value: snap }));
      }
    }

    rehydratingRef.current = true;

    const storedOwner = pickKV(snap, K.owner);
    const storedOp = pickKV(snap, K.operation);
    const storedLine = pickKV(snap, K.sessionLine);
    const storedView = pickKV(snap, K.activeView);
    const storedCar = pickKV(snap, K.selectedCarrierId);
    const storedPhase = pickKV(snap, K.selectedPhaseId);
    const storedDetPh = pickKV(snap, K.detailPhaseId);
    const storedBestMetric = pickKV(snap, K.bestMetric);

    const snapTender = pickKV(snap, K.tenderInfo);
    const snapPhases = pickKV(snap, K.phases);
    const snapQuotes = pickKV(snap, K.quotes);
    const snapLanes = pickKV(snap, K.lanes);
    const snapAllLanes = pickKV(snap, K.allLanes);
    const snapCarriers = pickKV(snap, K.carriers);
    const snapDistinctCarriers = pickKV(snap, K.distinctCarriersItems);
    const snapSummary = pickKV(snap, K.summary);
    const snapLaneSummaries = pickKV(snap, K.laneSummaries);
    const snapLaneSummariesAccepted = pickKV(snap, K.laneSummariesAccepted);
    const snapLaneSummariesAcceptedCurrent = pickKV(
      snap,
      K.laneSummariesAcceptedCurrent
    );
    const snapLastSyncAt = pickKV(snap, K.lastSyncAt);
    const snapTenderSchema = pickKV(snap, K.tenderSchemaOptions);
    const snapTenderFK = pickKV(snap, K.tenderFkData);

    if (storedOwner) {
      if (storedOp !== undefined) setOperation(storedOp);
      if (storedLine !== undefined) setSessionLine(storedLine);
      if (storedView !== undefined) setActiveView(storedView);
      if (storedCar !== undefined) setSelectedCarrierId(storedCar);
      if (storedPhase !== undefined) setSelectedPhaseId(storedPhase);
      if (storedDetPh !== undefined) setDetailPhaseId(storedDetPh);
      if (storedBestMetric !== undefined)
        setBestMetric(storedBestMetric || "bid");

      if (snapTender) setTenderInfo(snapTender);
      if (Array.isArray(snapPhases)) setPhases(snapPhases);
      if (Array.isArray(snapQuotes)) {
        setQuotes(snapQuotes);
        // Keep the derived subsets in sync when hydrating from snapshot.
        // Accepted quotes already come filtered from the BE endpoints, but we still
        // derive them here because the snapshot only stores the full list.
        setQuotesAccepted(snapQuotes.filter((q) => getQuoteAccepted(q)));
        setQuotesUnaccepted(snapQuotes.filter((q) => !getQuoteAccepted(q)));
      }
      if (Array.isArray(snapLanes)) setLanes(snapLanes);
      if (Array.isArray(snapAllLanes)) setAllLanes(snapAllLanes);
      if (Array.isArray(snapCarriers)) setCarriers(snapCarriers);
      if (Array.isArray(snapDistinctCarriers)) setDistinctCarriersItems(snapDistinctCarriers);
      if (Array.isArray(snapSummary)) setSummaryRows(snapSummary);
      if (Array.isArray(snapLaneSummaries))
        setLaneSummariesRows(snapLaneSummaries);
      if (Array.isArray(snapLaneSummariesAccepted))
        setLaneSummariesAcceptedRows(snapLaneSummariesAccepted);
      if (Array.isArray(snapLaneSummariesAcceptedCurrent))
        setLaneSummariesAcceptedCurrentRows(snapLaneSummariesAcceptedCurrent);
      if (snapLastSyncAt !== undefined) setLastSyncAt(snapLastSyncAt);
      if (Array.isArray(snapTenderSchema))
        setTenderSchemaOptions(snapTenderSchema);
      if (snapTenderFK && typeof snapTenderFK === "object")
        setTenderFkData(snapTenderFK);

      hydratedOnceRef.current = true;
      nsSnapshotRef.current = snap;
    } else {
      const seed = [
        { key: K.owner, value: String(selectedTab) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: {} },
        { key: K.phases, value: [] },
        { key: K.quotes, value: [] },
        { key: K.lanes, value: [] },
        { key: K.allLanes, value: [] },
        { key: K.carriers, value: [] },
        { key: K.summary, value: [] },
        { key: K.lastSyncAt, value: null },
        { key: K.tenderSchemaOptions, value: [] },
        { key: K.tenderFkData, value: {} },
        { key: K.laneSummaries, value: laneSummariesRows || [] },
        { key: K.laneSummariesAccepted, value: laneSummariesAcceptedRows || [] },
        { key: K.laneSummariesAcceptedCurrent, value: laneSummariesAcceptedCurrentRows || [] },

      ];
      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
      hydratedOnceRef.current = false;
    }

    setTimeout(() => {
      rehydratingRef.current = false;
    }, 0);

    prevTabRef.current = selectedTab || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  // =================================================================================================
  // Tender options schema (lightweight)
  // =================================================================================================
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if ((tenderSchemaOptions || []).length) return;

        const opts = await tenderOptionsService.options?.();
        if (cancelled) return;

        if (!isFailurePayload(opts)) {
          const schema = Array.isArray(opts) ? opts : [];
          setTenderSchemaOptions(schema);
        } else {
          setTenderSchemaOptions([]);
        }
        setTenderFkData({});
      } catch {
        setTenderSchemaOptions([]);
        setTenderFkData({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeView, tenderOptionsService, tenderSchemaOptions]);

  // =================================================================================================
  // Critical persistence: hard-save on component unmount + beforeunload
  // =================================================================================================
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
  }, [flushSnapshot]);

  // =================================================================================================
  // Debounced snapshot persistence
  // =================================================================================================
  useEffect(() => {
    if (!activeNsRef.current || rehydratingRef.current) return;
    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => flushSnapshot(), 200);
    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    };
  }, [
    operation,
    sessionLine,
    activeView,
    selectedCarrierId,
    selectedPhaseId,
    detailPhaseId,
    bestMetric,
    tenderInfo,
    phases,
    quotes,
    lanes,
    allLanes,
    carriers,
    summaryRows,
    lastSyncAt,
    tenderSchemaOptions,
    tenderFkData,
    flushSnapshot,
  ]);

  // =================================================================================================
  // Bootstrap: consume global handover once
  // =================================================================================================
  useEffect(() => {
    if (!isFirst) return;
    setIsFirst(false);

    dispatch(M_StateActions.removeM_State({ key: "operation" }));
    dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));

    const ns = stateKeyFrom(id);
    if (ns) {
      const seed = [
        { key: K.owner, value: String(id) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: tenderInfo },
        { key: K.phases, value: phases },
        { key: K.quotes, value: quotes },
        { key: K.lanes, value: lanes },
        { key: K.allLanes, value: allLanes },
        { key: K.carriers, value: carriers },
        { key: K.distinctCarriersItems, value: distinctCarriersItems },
        { key: K.summary, value: summaryRows },
        { key: K.lastSyncAt, value: lastSyncAt },
        { key: K.tenderSchemaOptions, value: tenderSchemaOptions },
        { key: K.tenderFkData, value: tenderFkData },
      ];
      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirst]);

  // =================================================================================================
  // Tab title (scoped to this page's owner tab)
  // =================================================================================================
  useEffect(() => {
    if (!renameTab) return;

    const ownerId =
      pickKV(currentNsSnapshot, K.owner) || activeTidRef.current || id;
    if (!ownerId) return;

    const base = t("TenderInQuotation");
    const viewKey =
      activeView === "carrier"
        ? t("CarrierDetails")
        : activeView === "lane"
          ? t("Lane")
          : activeView === "summary"
            ? t("Summary") || "Summary"
            : activeView === "inQuotation"
              ? t("InQuotation")
              : t("Details");

    const snapshotName = pickKV(currentNsSnapshot, K.tenderInfo)?.Name;
    const name = (tenderInfo?.Name || snapshotName || "").trim();

    const label = name
      ? `${base} — ${name} (${viewKey})`
      : `${base} (${viewKey})`;

    renameTab(String(ownerId), label);
  }, [activeView, tenderInfo?.Name, currentNsSnapshot, id, renameTab, t]);

  // =================================================================================================
  // Data loader (page-level)
  // - Loads tender + phases + carriers
  // - Loads quotes accepted/unaccepted in parallel (paged)
  // - Loads summary aggregates for lanes (paged) filtered by TenderId
  // =================================================================================================
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    // Consideramos hidratado se já carregámos uma vez o tender para esta linha
    const hasHydratedData =
      hydratedOnceRef.current && Number(tenderInfo?.Id) === Number(lineId);

    // Even if we have hydrated data (snapshot), we must periodically re-sync with backend.
    // Otherwise, after closing/reopening the page, accepted quotes can be out-of-date.
    const SYNC_MAX_AGE_MS = 30_000; // 30s
    const lastSync = Number(lastSyncAtRef.current);
    const isFreshEnough =
      Number.isFinite(lastSync) && Date.now() - lastSync < SYNC_MAX_AGE_MS;

    // Skip loading if we have hydrated data and it's still fresh enough
    if (hasHydratedData && refreshKey === 0 && isFreshEnough) return;

    const loadPhasesPaged = async ({ tender, lineId, isSpot }) => {
      let phasesList = Array.isArray(tender?.Phases) ? tender.Phases : [];

      // Non-spot: if phases are not embedded, fetch them paged
      if (!isSpot && phasesList.length === 0) {
        const pageSize = 20;
        let page = 1;
        const acc = [];
        const seen = new Set();
        const filter = encodeURIComponent(
          `TenderId eq ${lineId} and Inactive eq false`
        );

        while (true) {
          if (cancelled) break;

          const res = await phasesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) break;
          if (isFailurePayload(res)) break;

          const items = norm(res);
          if (!items.length) break;

          for (const it of items) {
            const pid = it?.Id;
            if (pid != null && !seen.has(pid)) {
              seen.add(pid);
              acc.push(it);
            }
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        phasesList = acc;
      }

      if (cancelled) return phasesList;
      setPhases(phasesList);

      // Auto-select most recent phase (non-spot)
      if (!isSpot && phasesList.length > 0 && selectedPhaseId == null) {
        const lastIds = phasesList
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid != null);

        const mostRecent =
          phasesList.find((p) => !lastIds.includes(p.Id)) ||
          phasesList[phasesList.length - 1];

        setSelectedPhaseId(mostRecent?.Id ?? null);
        if (detailPhaseId == null) setDetailPhaseId(mostRecent?.Id ?? null);
      }

      // Carriers
      if (!isSpot) {
        const results = await Promise.all(
          (phasesList || []).map(async (p) => {
            const res = await phaseCarriersService.getId(`${p.Id}/Carriers`);
            if (isFailurePayload(res)) return [];
            return norm(res);
          })
        );

        if (!cancelled) {
          const all = results.flat();
          const unique = [
            ...new Map(
              all.map((c) => [
                c.CarrierId || c.Id,
                {
                  CarrierId: c.CarrierId || c.Id,
                  CarrierName: c.CarrierName || c.Name,
                  Name: c.CarrierName || c.Name,
                },
              ])
            ).values(),
          ];
          setCarriers(unique);
        }
      } else {
        const spotCarriers = Array.isArray(tender?.Carriers)
          ? tender.Carriers
          : [];
        const mapped = spotCarriers
          .map((c) => {
            const carrierObj = c?.Carrier || {};
            return {
              CarrierId: c?.CarrierId || carrierObj?.Id || c?.Id,
              CarrierName: c?.CarrierName || carrierObj?.Name || "",
              Name: c?.CarrierName || carrierObj?.Name || "",
            };
          })
          .filter((x) => x.CarrierId != null);

        if (!cancelled) setCarriers(mapped);
      }

      return phasesList;
    };

    const loadQuotesByAcceptancePaged = async ({ lineId }) => {
      const pageSize = QUOTES_PAGE_SIZE;
      let page = 1;

      const acceptedAcc = [];
      const unacceptedAcc = [];
      const seen = new Set();

      while (true) {
        if (cancelled) break;

        const filterAccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${lineId} and IsAccepted eq true`
        );
        const filterUnaccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${lineId} and IsAccepted eq false`
        );

        const [resA, resB] = await Promise.all([
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterAccepted}`
          ),
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterUnaccepted}`
          ),
        ]);

        if (cancelled) break;

        const itemsA = isFailurePayload(resA) ? [] : norm(resA);
        const itemsB = isFailurePayload(resB) ? [] : norm(resB);

        for (const it of itemsA) {
          const qid = getQuoteId(it);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            acceptedAcc.push(it);
          }
        }

        for (const it of itemsB) {
          const qid = getQuoteId(it);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            unacceptedAcc.push(it);
          }
        }

        const totalPagesA = Number(
          resA?.TotalPages ??
          resA?.totalPages ??
          (itemsA.length < pageSize ? page : page + 1)
        );
        const totalPagesB = Number(
          resB?.TotalPages ??
          resB?.totalPages ??
          (itemsB.length < pageSize ? page : page + 1)
        );

        const totalPages = Math.max(totalPagesA, totalPagesB);
        if (page >= totalPages) break;
        page += 1;
      }

      const allQuotes = [...acceptedAcc, ...unacceptedAcc];

      if (!cancelled) {
        setQuotes(allQuotes);
        setQuotesAccepted(acceptedAcc);
        setQuotesUnaccepted(unacceptedAcc);
      }

      return { allQuotes, acceptedAcc, unacceptedAcc };
    };

    // Load persisted lane aggregates (/TenderLanes) - also acts as "ALL lanes" universe
    const loadLaneSummaryPaged = async ({ lineId }) => {
      const pageSize = SUMMARY_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(lineId)}`);

      while (true) {
        if (cancelled) break;

        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) break;
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? it?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          acc.push(it);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      if (!cancelled) setSummaryRows(acc);
      return acc;
    };

    const run = async () => {
      incLoad();

      try {
        const tender = await tenderService.getId(lineId);
        if (cancelled) return;

        if (isFailurePayload(tender)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(tender, t("ErrorLoadingData"))
          );
          return;
        }

        setTenderInfo(tender);

        const tenderTypeId = Number(tender?.TenderTypeId ?? 0);
        const tenderTypeName = String(tender?.TenderType ?? "").toLowerCase();
        const isSpot = tenderTypeId === 1 || tenderTypeName === "spot";

        const phasesPromise = loadPhasesPaged({ tender, lineId, isSpot });
        const quotesPromise = loadQuotesByAcceptancePaged({ lineId });
        const summaryPromise = loadLaneSummaryPaged({ lineId });

        await Promise.allSettled([
          phasesPromise,
          quotesPromise,
          summaryPromise,
        ]);

        if (!cancelled) {
          hydratedOnceRef.current = true;
          setLastSyncAt(Date.now());
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
        }
      } finally {
        decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, refreshKey, baseUrl]);

  // quotesAccepted already comes filtered from the BE (IsAccepted eq true)
  // - We keep it separate from hvQuotesAll so we don't need to re-filter accepted quotes on the FE.
  // - If this dataset is ever empty (e.g. before load), we default to [].
  const hvQuotesAccepted = useMemo(
    () => (quotesAccepted && quotesAccepted.length ? quotesAccepted : []),
    [quotesAccepted]
  );

  // =================================================================================================
  // Hydrated fallbacks
  // =================================================================================================
  const hvTender = useMemo(
    () =>
      Object.keys(tenderInfo || {}).length
        ? tenderInfo
        : pickKV(currentNsSnapshot, K.tenderInfo) || {},
    [tenderInfo, currentNsSnapshot]
  );

  const hvPhases = useMemo(
    () =>
      phases && phases.length
        ? phases
        : pickKV(currentNsSnapshot, K.phases) || [],
    [phases, currentNsSnapshot]
  );

  const hvQuotesAll = useMemo(
    () =>
      quotes && quotes.length
        ? quotes
        : pickKV(currentNsSnapshot, K.quotes) || [],
    [quotes, currentNsSnapshot]
  );

  // lanes = current lanes (from /flat)
  const hvLanes = useMemo(
    () =>
      lanes && lanes.length ? lanes : pickKV(currentNsSnapshot, K.lanes) || [],
    [lanes, currentNsSnapshot]
  );

  // allLanes = all tender lanes (from /TenderLanes)
  const hvAllLanes = useMemo(
    () =>
      allLanes && allLanes.length
        ? allLanes
        : pickKV(currentNsSnapshot, K.allLanes) || [],
    [allLanes, currentNsSnapshot]
  );

  // persisted aggregates fallback
  const hvSummaryRows = useMemo(
    () =>
      summaryRows && summaryRows.length
        ? summaryRows
        : pickKV(currentNsSnapshot, K.summary) || [],
    [summaryRows, currentNsSnapshot]
  );


  // Lane summaries (aggregated per lane)
  const hvLaneSummaries = useMemo(
    () =>
      laneSummariesRows && laneSummariesRows.length
        ? laneSummariesRows
        : pickKV(currentNsSnapshot, K.laneSummaries) || [],
    [laneSummariesRows, currentNsSnapshot]
  );

  const hvLaneSummariesAccepted = useMemo(
    () =>
      laneSummariesAcceptedRows && laneSummariesAcceptedRows.length
        ? laneSummariesAcceptedRows
        : pickKV(currentNsSnapshot, K.laneSummariesAccepted) || [],
    [laneSummariesAcceptedRows, currentNsSnapshot]
  );

  // current phase accepted summaries
  const hvLaneSummariesAcceptedCurrent = useMemo(
    () =>
      Array.isArray(laneSummariesAcceptedCurrentRows)
        ? laneSummariesAcceptedCurrentRows
        : [],
    [laneSummariesAcceptedCurrentRows]
  );

  // =================================================================================================
  // Derived data for inline views
  // =================================================================================================
  const isSpotTender =
    Number(hvTender?.TenderTypeId) === 1 ||
    String(hvTender?.TenderType || "").toLowerCase() === "spot";

  const isTenderFinished = useMemo(() => {
    const v = hvTender?.IsFinished ?? hvTender?.isFinished ?? false;
    return v === true;
  }, [hvTender]);

  // Selects the phase used as the lane visibility cutoff in non-spot tenders
  const lanesPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return activeView === "carrier" ? detailPhaseId : selectedPhaseId;
  }, [isSpotTender, activeView, detailPhaseId, selectedPhaseId]);

  // =================================================================================================
  // Current open phase id (Non-Spot only)
  // - We ONLY consider as "current" a phase that:
  //     * IsLastPhase === true
  //     * IsFinished === false
  // - If all phases are finished (or Spot tender), this returns null,
  //   meaning TenderSummaryTable will NOT add any "delta" on top of the snapshot.
  // =================================================================================================
  const currentOpenPhaseId = useMemo(() => {
    if (isSpotTender) return null;

    const phases = Array.isArray(hvPhases) ? hvPhases : [];

    const current = phases.find((p) => {
      const isLast =
        p?.IsLastPhase ?? p?.isLastPhase ?? false;
      const isFinished =
        p?.IsFinished ?? p?.isFinished ?? false;

      return isLast && !isFinished;
    });

    if (!current) return null;

    const rawId =
      current?.Id ??
      current?.id ??
      current?.TenderPhaseId ??
      current?.tenderPhaseId ??
      null;

    return rawId != null ? Number(rawId) : null;
  }, [isSpotTender, hvPhases]);

  // =================================================================================================
  // LaneSummaries (aggregated per lane)
  // - Fetched from BE so we don't compute metrics on FE.
  //
  // We maintain THREE datasets:
  // - hvLaneSummaries
  //     * /TenderQuotes/laneSummaries
  //     * Phase-aware for the ACTIVE inline view (selected/detail phase).
  //     * Used mainly for charts / per-phase analysis.
  //
  // - hvLaneSummariesAccepted
  //     * /TenderQuotes/laneSummaries/accepted with ONLY TenderId filter.
  //     * Aggregated per Tender x Lane (all phases collapsed by the BE).
  //     * Used by ViewLaneTable to show best metrics for ALL accepted quotes in the tender.
  //
  // - hvLaneSummariesAcceptedCurrent
  //     * /TenderQuotes/laneSummaries/accepted with TenderId + current TenderPhaseId (lastPhaseId).
  //     * Aggregated per Tender x TenderPhase x Lane.
  //     * Used by TenderSummaryTable as the "delta" of the CURRENT phase,
  //       to be merged with the baseline snapshot from /TenderLanes/flat.
  // =================================================================================================
  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id ?? null;
    if (!tenderId) return;

    const viewPhaseId = isSpotTender ? null : lanesPhaseId;

    // For summaries, "current phase" is only the open last phase (if any).
    // If the last phase is already finished and there is no new one,
    // currentPhaseId will be null, so we DO NOT add any delta on top
    // of the /TenderLanes/flat snapshot.
    const currentPhaseId = isSpotTender ? null : currentOpenPhaseId;

    const norm = (res) =>
      Array.isArray(res?.Items) ? res.Items : Array.isArray(res) ? res : [];

    const fetchPaged = async ({ service, filterParts, pageSize }) => {
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(filterParts.join(" and "));

      while (true) {
        if (cancelled) return [];

        const res = await service.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) return [];
        if (isFailurePayload(res)) return [];

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.LaneId ?? it?.laneId ?? it?.Id ?? it?.id ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);
          acc.push(it);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      return acc;
    };

    const run = async () => {
      const shouldShowLoading = activeView === "carrier" || activeView === "lane";
      if (shouldShowLoading) incLoad();

      try {
        const commonFilterParts = [
          `TenderId eq ${Number(tenderId)}`,
          "Inactive eq false",
        ];

        const pageSize = 500;

        // /laneSummaries: phase-aware for the ACTIVE inline view (selected/detail phase).
        const allFilterParts = [...commonFilterParts];
        if (viewPhaseId != null) {
          allFilterParts.push(`TenderPhaseId eq ${Number(viewPhaseId)}`);
        }

        // /laneSummaries/accepted: ALL accepted quotes for this Tender, ALL phases collapsed by BE.
        const acceptedAllFilterParts = [...commonFilterParts];
        // NOTE: DO NOT add TenderPhaseId here on purpose.

        // /laneSummaries/accepted for CURRENT phase (Non-Spot only):
        // used by TenderSummaryTable as the "delta" of the current phase.
        const acceptedCurrentFilterParts = [...commonFilterParts];
        if (!isSpotTender && currentPhaseId != null) {
          acceptedCurrentFilterParts.push(
            `TenderPhaseId eq ${Number(currentPhaseId)}`
          );
        } else if (isSpotTender) {
          // For Spot tenders, we keep the current-phase dataset empty
          // (the snapshot already covers everything).
          acceptedCurrentFilterParts.push("TenderPhaseId eq null");
        }

        const [allRows, acceptedAllRows, acceptedCurrentRows] =
          await Promise.all([
            fetchPaged({
              service: laneSummariesService,
              filterParts: allFilterParts,
              pageSize,
            }),
            fetchPaged({
              service: laneSummariesAcceptedService,
              filterParts: acceptedAllFilterParts,
              pageSize,
            }),
            !isSpotTender && currentPhaseId != null
              ? fetchPaged({
                service: laneSummariesAcceptedService,
                filterParts: acceptedCurrentFilterParts,
                pageSize,
              })
              : Promise.resolve([]),
          ]);

        if (cancelled) return;

        setLaneSummariesRows(allRows); // /laneSummaries (phase-aware)
        setLaneSummariesAcceptedRows(acceptedAllRows); // /accepted (Tender-wide, all phases)
        setLaneSummariesAcceptedCurrentRows(acceptedCurrentRows); // /accepted (current phase only)
      } catch {
        if (!cancelled) {
          setLaneSummariesRows([]);
          setLaneSummariesAcceptedRows([]);
          setLaneSummariesAcceptedCurrentRows([]);
        }
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, isSpotTender, lanesPhaseId, refreshKey, activeView, laneSummariesService, laneSummariesAcceptedService, incLoad, decLoad, hvPhases, currentOpenPhaseId]);

  // =================================================================================================
  // last phase id (true last phase, independent of currently selected phase)
  // - Used to enforce toggle rules in ViewLaneTable without relying on BE fields.
  // =================================================================================================
  const lastPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return resolveLastPhaseId(hvPhases);
  }, [isSpotTender, hvPhases]);

  // =================================================================================================
  // Quotes enriched with IsLastPhase flag (flattened)
  // - This prevents "always disabled" checkboxes in Non-Spot when BE doesn't send IsLastPhase.
  // - We keep the original hvQuotesAll for other legacy views unless needed.
  // =================================================================================================
  const hvQuotesAllEnriched = useMemo(
    () => withIsLastPhase(hvQuotesAll, lastPhaseId),
    [hvQuotesAll, lastPhaseId]
  );

  const hvQuotesAcceptedEnriched = useMemo(() => {
    // IMPORTANT:
    // - Accepted quotes already come filtered from the BE (IsAccepted eq true).
    // - We do not re-filter accepted quotes on the FE.
    // - We also do not re-filter inactive here (Inactive=false is implicit / should be filtered by BE).
    return withIsLastPhase(hvQuotesAccepted, lastPhaseId);
  }, [hvQuotesAccepted, lastPhaseId]);

  // =================================================================================================
  // Lane best metrics map (per lane, for ALL accepted quotes in the Tender).
  // - Built from hvLaneSummariesAccepted = /laneSummaries/accepted filtered only by TenderId.
  // - For each LaneId we aggregate across ALL phases:
  //     * bestBidValue  = MIN of MinBidValue
  //     * bestLoadsValue = MAX of MaxLoads
  //     * bestEtdValue   = MIN of MinEstimatedTransitDays
  // - ViewLaneTable will use this map so that BestMetric always reflects the
  //   best accepted values across the whole tender (current + previous phases).
  // =================================================================================================
  const laneBestMap = useMemo(() => {
    const agg = new Map();

    const rows = Array.isArray(hvLaneSummariesAccepted)
      ? hvLaneSummariesAccepted
      : [];

    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    for (const r of rows) {
      const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;

      let cur = agg.get(laneId);
      if (!cur) {
        cur = {
          minBid: null,
          maxLoads: null,
          minEtd: null,
        };
      }

      const minBidRow = asNum(r?.MinBidValue ?? r?.minBidValue);
      if (minBidRow != null) {
        if (cur.minBid == null || minBidRow < cur.minBid) cur.minBid = minBidRow;
      }

      const maxLoadsRow = asNum(r?.MaxLoads ?? r?.maxLoads);
      if (maxLoadsRow != null) {
        if (cur.maxLoads == null || maxLoadsRow > cur.maxLoads)
          cur.maxLoads = maxLoadsRow;
      }

      const minEtdRow = asNum(
        r?.MinEstimatedTransitDays ?? r?.minEstimatedTransitDays
      );
      if (minEtdRow != null) {
        if (cur.minEtd == null || minEtdRow < cur.minEtd) cur.minEtd = minEtdRow;
      }

      agg.set(laneId, cur);
    }

    const map = new Map();
    for (const [laneId, v] of agg.entries()) {
      map.set(laneId, {
        bestBidValue: v.minBid,
        bestLoadsValue: v.maxLoads,
        bestEtdValue: v.minEtd,
      });
    }

    return map;
  }, [hvLaneSummariesAccepted]);

  // =================================================================================================
  // LOAD: distinct carriers (carriers that HAVE quotes), already filtered on the backend
  // - Spot: filter only by TenderId
  // - Non-Spot: filter by TenderId + currently relevant PhaseId (selectedPhaseId on main view, detailPhaseId on carrier view)
  // =================================================================================================
  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id;
    if (!tenderId) {
      setDistinctCarriersItems([]);
      return;
    }

    // Which phase should drive the carrier list?
    // - On the main (inQuotation) view we use the selected phase (if any)
    // - On the carrier view we use the detail phase (if any)
    const phaseId =
      isSpotTender
        ? null
        : activeView === "carrier"
          ? detailPhaseId
          : selectedPhaseId;

    const fetchDistinctCarriers = async () => {
      const shouldShowLoading = activeView === "carrier";
      if (shouldShowLoading) incLoad();

      try {
        const filterParts = [`TenderId eq ${tenderId}`, `Inactive eq false`];
        if (phaseId != null) filterParts.push(`TenderPhaseId eq ${phaseId}`);

        const filter = encodeURIComponent(filterParts.join(" and "));

        // IMPORTANT (perf):
        // - This endpoint can return hundreds/thousands of carriers-with-quotes.
        // - Always page server-side and accumulate ALL pages (UI needs full list).
        const pageSize = DISTINCT_CARRIERS_PAGE_SIZE;
        let page = 1;

        const acc = [];
        const seen = new Set();

        while (true) {
          if (cancelled) return;

          const res = await distinctCarriersService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) return;
          if (isFailurePayload(res)) break;

          const items =
            res?.Items ??
            res?.items ??
            res?.Data?.Items ??
            res?.data?.items ??
            [];

          if (!Array.isArray(items) || items.length === 0) break;

          for (const row of items) {
            // Defensive: avoid duplicates across pages (carrierId is the natural key)
            const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
            const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;
            if (carrierId == null) continue;

            const key = String(carrierId);
            if (seen.has(key)) continue;
            seen.add(key);

            acc.push(row);
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        setDistinctCarriersItems(acc);
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    fetchDistinctCarriers().catch(() => {
      if (!cancelled) setDistinctCarriersItems([]);
    });

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, isSpotTender, activeView, selectedPhaseId, detailPhaseId, refreshKey, distinctCarriersReloadKey, distinctCarriersService, incLoad, decLoad]);

  // =================================================================================================
  // Lanes loader (Spot + Non-Spot) via /flat  => CURRENT LANES
  // =================================================================================================
  const loadLanesPaged = useCallback(
    async ({ lineId, isSpot, phaseId }) => {
      const pageSize = LANES_PAGE_SIZE_FLAT;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const baseFilter = isSpot
        ? `TenderId eq ${Number(lineId)}`
        : `TenderId eq ${Number(
          lineId
        )} and SatisfiedInTenderPhaseId eq ${Number(phaseId)}`;

      const qFilter = encodeURIComponent(baseFilter);

      while (true) {
        const lanesRes = await tenderLanesFlatService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${qFilter}`
        );

        if (isFailurePayload(lanesRes)) break;

        const items = norm(lanesRes);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? null;
          if (laneId == null || seen.has(laneId)) continue;

          seen.add(laneId);

          const origin = String(
            it?.sourceLocation ?? it?.SourceLocation ?? ""
          ).trim();
          const destination = String(
            it?.destinationLocation ?? it?.DestinationLocation ?? ""
          ).trim();

          acc.push({
            ...it,

            LaneId: Number(laneId),

            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,

            IsActive:
              it?.inactive != null
                ? !it.inactive
                : it?.Inactive != null
                  ? !it.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          lanesRes?.TotalPages ??
          lanesRes?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setLanes(acc);
      return acc;
    },
    [tenderLanesFlatService]
  );

  // =================================================================================================
  // ALL lanes loader (via /TenderLanes)  => GLOBAL LANES
  // =================================================================================================
  const loadAllLanesPaged = useCallback(
    async ({ lineId }) => {
      const pageSize = ALL_LANES_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(lineId)}`);

      while (true) {
        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? it?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          // Try to extract origin/destination from aggregate row (payload can vary).
          const originRaw =
            it?.sourceLocation ??
            it?.SourceLocation ??
            it?.origin ??
            it?.Origin ??
            it?.laneSource ??
            it?.LaneSource ??
            it?.LaneOrigin ??
            it?.laneOrigin ??
            "";

          const destinationRaw =
            it?.destinationLocation ??
            it?.DestinationLocation ??
            it?.destination ??
            it?.Destination ??
            it?.laneDestination ??
            it?.LaneDestination ??
            it?.LaneDestinationName ??
            it?.laneDestinationName ??
            "";

          const origin = String(originRaw || "").trim();
          const destination = String(destinationRaw || "").trim();

          // Map into the same lane shape used across legacy UI.
          acc.push({
            ...it,

            LaneId: Number(laneId),

            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,

            // Aggregates usually don't have an "inactive" concept per lane; assume active unless specified.
            IsActive:
              it?.inactive != null
                ? !it.inactive
                : it?.Inactive != null
                  ? !it.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setAllLanes(acc);
      return acc;
    },
    [tenderLanesSummaryService]
  );

  // =================================================================================================
  // Lanes refresh (phase-aware for non-spot tenders) => CURRENT lanes
  // =================================================================================================
  useEffect(() => {
    let cancelled = false;

    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        if (isSpotTender) {
          const res = await loadLanesPaged({
            lineId,
            isSpot: true,
            phaseId: null,
          });
          if (cancelled) return;
          if (Array.isArray(res)) setLanes(res);
          return;
        }

        if (lanesPhaseId == null) return;

        const res = await loadLanesPaged({
          lineId,
          isSpot: false,
          phaseId: lanesPhaseId,
        });

        if (cancelled) return;
        if (Array.isArray(res)) setLanes(res);
      } catch {
        if (!cancelled) setLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, isSpotTender, lanesPhaseId, loadLanesPaged]);

  // =================================================================================================
  // ALL lanes refresh (independent of phase) => GLOBAL lanes
  // =================================================================================================
  useEffect(() => {
    let cancelled = false;

    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        const res = await loadAllLanesPaged({ lineId });
        if (cancelled) return;
        if (Array.isArray(res)) setAllLanes(res);
      } catch {
        if (!cancelled) setAllLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, loadAllLanesPaged]);

  // =================================================================================================
  // Summary rows for TenderSummaryTable
  // - Baseline: persisted lane aggregates from /TenderLanes/flat (hvSummaryRows),
  //   which represent "all phases up to the last closed phase".
  // - Current delta (Non-Spot only): accepted lane summaries from the CURRENT phase
  //   taken from hvLaneSummariesAcceptedCurrent (/laneSummaries/accepted with TenderPhaseId = lastPhaseId).
  // - For each lane:
  //     * Totals (quotes, loads, bid value) = baseline + current phase accepted
  //     * Min/Max (bid, loads, ETD)         = min/max between baseline and current phase accepted
  // - Satisfied metadata (SatisfiedAt, SatisfiedInPhase) comes from the baseline snapshot.
  // =================================================================================================
  const summaryLaneRows = useMemo(() => {
    const lanes = Array.isArray(hvAllLanes) ? hvAllLanes : [];
    const baseline = Array.isArray(hvSummaryRows) ? hvSummaryRows : [];
    const acceptedCurrent = Array.isArray(hvLaneSummariesAcceptedCurrent)
      ? hvLaneSummariesAcceptedCurrent
      : [];

    const baselineByLane = new Map();
    for (const r of baseline) {
      const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;
      baselineByLane.set(laneId, r);
    }

    // Current phase accepted metrics (Non-Spot only, already filtered by TenderPhaseId at fetch time)
    const currentByLane = new Map();
    if (!isSpotTender && lastPhaseId != null) {
      for (const r of acceptedCurrent) {
        const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
        const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
        if (!Number.isFinite(laneId)) continue;
        // There should be at most one row per LaneId for the current phase, but merge defensively
        const existing = currentByLane.get(laneId);
        if (!existing) {
          currentByLane.set(laneId, { ...r });
        } else {
          const sum = (a, b) => {
            const na = Number(a);
            const nb = Number(b);
            const sa = Number.isFinite(na) ? na : 0;
            const sb = Number.isFinite(nb) ? nb : 0;
            return sa + sb;
          };
          const min = (a, b) => {
            const na = Number(a);
            const nb = Number(b);
            const va = Number.isFinite(na) && na !== 0 ? na : null;
            const vb = Number.isFinite(nb) && nb !== 0 ? nb : null;
            if (va == null && vb == null) return null;
            if (va == null) return vb;
            if (vb == null) return va;
            return Math.min(va, vb);
          };
          const max = (a, b) => {
            const na = Number(a);
            const nb = Number(b);
            const va = Number.isFinite(na) ? na : null;
            const vb = Number.isFinite(nb) ? nb : null;
            if (va == null && vb == null) return null;
            if (va == null) return vb;
            if (vb == null) return va;
            return Math.max(va, vb);
          };

          currentByLane.set(laneId, {
            ...existing,
            TotalQuotes: sum(existing.TotalQuotes, r.TotalQuotes),
            TotalLoads: sum(existing.TotalLoads, r.TotalLoads),
            TotalBidValue: sum(existing.TotalBidValue, r.TotalBidValue),
            MinBidValue: min(existing.MinBidValue, r.MinBidValue),
            MaxBidValue: max(existing.MaxBidValue, r.MaxBidValue),
            MinLoads: min(existing.MinLoads, r.MinLoads),
            MaxLoads: max(existing.MaxLoads, r.MaxLoads),
            MinEstimatedTransitDays: min(
              existing.MinEstimatedTransitDays,
              r.MinEstimatedTransitDays
            ),
            MaxEstimatedTransitDays: max(
              existing.MaxEstimatedTransitDays,
              r.MaxEstimatedTransitDays
            ),
          });
        }
      }
    }

    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const asMin = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n === 0) return null;
      return n;
    };
    const mergeMin = (baseVal, curVal) => {
      const b = asMin(baseVal);
      const c = asMin(curVal);
      if (b == null && c == null) return null;
      if (b == null) return c;
      if (c == null) return b;
      return Math.min(b, c);
    };
    const mergeMax = (baseVal, curVal) => {
      const b = asNum(baseVal);
      const c = asNum(curVal);
      if (b == null && c == null) return null;
      if (b == null) return c;
      if (c == null) return b;
      return Math.max(b, c);
    };

    const phaseById = new Map((hvPhases || []).map((p) => [p.Id, p]));

    const rows = [];

    for (const lane of lanes) {
      const laneId = Number(getLaneId(lane));
      if (!Number.isFinite(laneId)) continue;

      const base = baselineByLane.get(laneId) || null;
      const cur =
        !isSpotTender && lastPhaseId != null
          ? currentByLane.get(laneId) || null
          : null;

      // Origin / destination: prefer hvAllLanes, fallback baseline
      const origin =
        (getLaneOrigin(lane) || "").trim() ||
        String(
          base?.SourceLocation ??
          base?.sourceLocation ??
          base?.LaneSource ??
          base?.laneSource ??
          base?.Origin ??
          base?.origin ??
          ""
        ).trim();

      const destination =
        (getLaneDestination(lane) || "").trim() ||
        String(
          base?.DestinationLocation ??
          base?.destinationLocation ??
          base?.LaneDestination ??
          base?.laneDestination ??
          base?.Destination ??
          base?.destination ??
          ""
        ).trim();

      // Totals = baseline + current phase
      const baseTotalQuotes = asNum(base?.TotalQuotes ?? base?.totalQuotes) ?? 0;
      const curTotalQuotes = asNum(cur?.TotalQuotes ?? cur?.totalQuotes) ?? 0;
      const totalQuotes = baseTotalQuotes + curTotalQuotes;

      const baseTotalLoads = asNum(base?.TotalLoads ?? base?.totalLoads) ?? 0;
      const curTotalLoads = asNum(cur?.TotalLoads ?? cur?.totalLoads) ?? 0;
      const totalLoads = baseTotalLoads + curTotalLoads;

      const baseTotalBid = asNum(base?.TotalBidValue ?? base?.totalBidValue);
      const curTotalBid = asNum(cur?.TotalBidValue ?? cur?.totalBidValue);
      let totalBidValue = null;
      if (baseTotalBid != null || curTotalBid != null) {
        totalBidValue = (baseTotalBid ?? 0) + (curTotalBid ?? 0);
      }

      const minBidValue = mergeMin(
        base?.MinBidValue ?? base?.minBidValue,
        cur?.MinBidValue ?? cur?.minBidValue
      );
      const maxBidValue = mergeMax(
        base?.MaxBidValue ?? base?.maxBidValue,
        cur?.MaxBidValue ?? cur?.maxBidValue
      );

      const minLoads = mergeMin(
        base?.MinLoads ?? base?.minLoads,
        cur?.MinLoads ?? cur?.minLoads
      );
      const maxLoads = mergeMax(
        base?.MaxLoads ?? base?.maxLoads,
        cur?.MaxLoads ?? cur?.maxLoads
      );

      const minEstimatedTransitDays = mergeMin(
        base?.MinEstimatedTransitDays ?? base?.minEstimatedTransitDays,
        cur?.MinEstimatedTransitDays ?? cur?.minEstimatedTransitDays
      );
      const maxEstimatedTransitDays = mergeMax(
        base?.MaxEstimatedTransitDays ?? base?.maxEstimatedTransitDays,
        cur?.MaxEstimatedTransitDays ?? cur?.maxEstimatedTransitDays
      );

      // Satisfied metadata ONLY from baseline snapshot
      const baseSatisfiedAt =
        base?.SatisfiedAt ?? base?.satisfiedAt ?? null;
      const baseSatisfiedPhaseId =
        base?.SatisfiedInTenderPhaseId ??
        base?.satisfiedInTenderPhaseId ??
        null;

      const basePhase =
        baseSatisfiedPhaseId != null
          ? phaseById.get(baseSatisfiedPhaseId) || null
          : null;
      const satisfiedPhaseName =
        basePhase?.Name ?? basePhase?.name ?? null;

      const isSatisfied = !!baseSatisfiedAt;

      rows.push({
        laneId,
        origin: origin || "-",
        destination: destination || "-",
        totalQuotes,
        totalBidValue,
        maxBidValue,
        minBidValue,
        totalLoads,
        maxLoads,
        minLoads,
        maxEstimatedTransitDays,
        minEstimatedTransitDays,
        isSatisfied,
        satisfiedAt: baseSatisfiedAt,
        satisfiedInPhase: satisfiedPhaseName,
      });
    }

    return rows;
  }, [
    hvAllLanes,
    hvSummaryRows,
    hvLaneSummariesAcceptedCurrent,
    hvPhases,
    isSpotTender,
    lastPhaseId,
  ]);

  // =================================================================================================
  // UI: carrier detail refresh hook
  // =================================================================================================
  const refreshCarrierDetailTable = useCallback((carrierId, phaseId) => {
    setSelectedCarrierId(carrierId);
    setActiveView("carrier");
    if (phaseId != null) setDetailPhaseId(phaseId);
    setRefreshKey((prev) => prev + 1);
  }, []);
  // =================================================================================================
  // UI: when changing the Phase in the Carrier detail view (Non-Spot),
  // show loading and reset selected carrier so we don't show stale/empty tables.
  // =================================================================================================
  const handleDetailPhaseChange = useCallback(
    (newPhaseId) => {
      // Phase drives both: (1) which carriers are available and (2) which quotes are shown.
      setDistinctCarriersItems([]);
      setSelectedCarrierId(null);
      setDetailPhaseId(newPhaseId ?? null);

      // Force re-fetch even when selecting the same phase again.
      setDistinctCarriersReloadKey((prev) => prev + 1);
    },
    []
  );

  // =================================================================================================
  // UI: quote accept/unaccept
  // =================================================================================================
  const setQuoteAccepted = useCallback(
    async ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;
      if (acceptBusyIdsRef.current.has(quoteId)) return;

      // Show global loading while accepting/unaccepting so the UI doesn't "build" in front of the user.
      incLoad();

      setAcceptBusyIds((prev) => {
        const next = new Set(prev);
        next.add(quoteId);
        return next;
      });

      try {
        const action = nextAccepted ? "accept" : "unaccept";

        // POST /api/TenderQuotes/update/{QuoteId}/{accept|unaccept}
        const res = await tenderQuotesUpdateService.postQuery(
          `/${quoteId}/${action}`
        );

        if (isFailurePayload(res)) {
          toastRef.current?.("error", "", apiMessage(res, t("Erro")));
          return;
        }

        // FAST PATH: update local quotes state immediately.
        // We keep quotesAccepted / quotesUnaccepted derived from the updated full list,
        // so downstream tables always receive the correct subsets.
        setQuotes((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list.map((q) => {
            const id = getQuoteId(q);
            if (id !== quoteId) return q;
            return {
              ...q,
              IsAccepted: nextAccepted,
              isAccepted: nextAccepted,
            };
          });

          setQuotesAccepted(next.filter((q) => getQuoteAccepted(q)));
          setQuotesUnaccepted(next.filter((q) => !getQuoteAccepted(q)));

          return next;
        });

        setLastSyncAt(Date.now());

        // IMPORTANT:
        // After accepting/unaccepting quotes, lane summaries must be refreshed,
        // otherwise metrics (totals, min/max, satisfied status) can be stale on the next render.
        // This will re-trigger the laneSummaries + laneSummaries/accepted loader effect.
        setRefreshKey((prev) => prev + 1);
      } catch (err) {
        toastRef.current?.("error", "", apiMessage(err, t("Erro")));
      } finally {
        // Refresh carrier list (accepted counts can change) and stop loading.
        setDistinctCarriersReloadKey((prev) => prev + 1);
        decLoad();

        setAcceptBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(quoteId);
          return next;
        });
      }
    },
    [decLoad, incLoad, t, tenderQuotesUpdateService]
  );

  // =================================================================================================
  // UI: finish tender flow
  // =================================================================================================
  const [showCreatePhaseConfirm, setShowCreatePhaseConfirm] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  const handleFinish = useCallback(() => {
    if (!sessionLine) return;
    setShowFinishConfirm(true);
  }, [sessionLine]);

  const confirmFinishTender = useCallback(() => {
    // Close confirmation modal first
    setShowFinishConfirm(false);

    // safety guard to avoid null reference on first render or unexpected state
    if (!sessionLine?.Id) {
      return;
    }

    incLoad();

    tenderServiceUpdate
      .update(sessionLine.Id, { isFinished: true })
      .then((res) => {
        if (isFailurePayload(res)) {
          toastRef.current?.("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        toastRef.current?.("success", "", t("TenderFinished"));
        handleTabClose(id);
      })
      .catch((err) =>
        toastRef.current?.("error", "", apiMessage(err, t("ErrorSaving")))
      )
      .finally(() => {
        decLoad();
      });
  }, [incLoad, decLoad, tenderServiceUpdate, sessionLine?.Id, t, handleTabClose, id]);

  // =================================================================================================
  // UI: create phase flow
  // =================================================================================================
  const confirmCreatePhase = useCallback(() => {
    if (!sessionLine?.Id) return;
    setShowCreatePhaseConfirm(false);
    incLoad();

    try {
      let lastPhase = null;
      if (phases.length === 1) lastPhase = phases[0];
      else if (phases.length > 1) {
        const lastIds = phases
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid !== null);
        lastPhase = phases.find((p) => !lastIds.includes(p.Id)) || null;
      }

      navigate("/home/create-tenders", {
        state: {
          isCreatePhase: true,
          tenderInfo,
          lastPhase,
          phases,
          quotes: hvQuotesAll,
          lanes,
          carriers,
          distinctCarriersItems,
        },
      });
    } catch {
      toastRef.current?.("error", "", t("ErrorFinishingLastPhase"));
    } finally {
      decLoad();
    }
  }, [
    sessionLine?.Id,
    incLoad,
    phases,
    navigate,
    tenderInfo,
    hvQuotesAll,
    lanes,
    carriers,
    distinctCarriersItems,
    t,
    decLoad,
  ]);

  const carriersWithQuotes = useMemo(() => {
    // IMPORTANT:
    // Carriers-with-quotes must come from the BE (/distinctCarriers) to avoid legacy O(n*m)
    // filtering on the frontend. If the BE payload is not available yet, we return an empty
    // list (UI can show loading/empty state).
    if (!Array.isArray(distinctCarriersItems) || !distinctCarriersItems.length)
      return [];

    return distinctCarriersItems
      .map((row) => {
        const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
        const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;

        return {
          // Keep normalized id/name fields used by selects/tables
          CarrierId: carrierId,
          CarrierName: carrier?.CarrierName ?? carrier?.Name ?? "",
          Name: carrier?.CarrierName ?? carrier?.Name ?? "",

          // Keep the original carrier fields for tables that need them
          ...carrier,

          // Carry counts (optional UI badges)
          QuotesCount: row?.QuotesCount ?? row?.quotesCount ?? null,
          QuotesAcceptedCount:
            row?.QuotesAcceptedCount ?? row?.quotesAcceptedCount ?? null,
        };
      })
      .filter((c) => c.CarrierId != null);
  }, [distinctCarriersItems]);

  // =================================================================================================
  // UX: when opening the Carrier view without a pre-selected carrier,
  // auto-select the first carrier available (so the table is not empty).
  // =================================================================================================
  useEffect(() => {
    if (activeView !== "carrier") return;
    if (selectedCarrierId != null) return;
    if (!Array.isArray(carriersWithQuotes) || carriersWithQuotes.length === 0)
      return;

    const firstId = carriersWithQuotes[0]?.CarrierId ?? carriersWithQuotes[0]?.Id;
    if (firstId != null) setSelectedCarrierId(firstId);
  }, [activeView, selectedCarrierId, carriersWithQuotes, setSelectedCarrierId]);


  // Pre-index quotes by phase once (O(n) once). Switching phase becomes O(1) and avoids "building" UI in slow motion.
  const quotesByPhaseId = useMemo(() => {
    const map = new Map();
    if (isSpotTender) return map;

    for (const q of hvQuotesAll || []) {
      const pid = getQuotePhaseId(q);
      if (pid == null) continue;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(q);
    }
    return map;
  }, [hvQuotesAll, isSpotTender]);

  const detailQuotes = useMemo(() => {
    if (isSpotTender) return hvQuotesAll;
    if (detailPhaseId == null) return [];
    return quotesByPhaseId.get(detailPhaseId) || [];
  }, [hvQuotesAll, isSpotTender, detailPhaseId, quotesByPhaseId]);

  // =================================================================================================
  // Render
  // =================================================================================================
  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("InQuotation")}
              icon={
                <i
                  className="pi pi-chart-line iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("inQuotation")}
              label={t("InQuotation")}
            />

            <MButton
              tooltip={t("Carrier")}
              icon={
                <i
                  className="pi pi-truck"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("carrier")}
              label={t("ViewCarriers")}
            />

            <MButton
              tooltip={t("Lane")}
              icon={
                <i
                  className="pi pi-arrow-right-arrow-left iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("lane")}
              label={t("ViewLanes")}
            />

            <MButton
              tooltip={t("Summary") || "Summary"}
              icon={
                <i
                  className="pi pi-chart-bar iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("summary")}
              label={t("ViewSummary") || "Summary"}
            />

            {!isSpotTender && (
              <MButton
                tooltip={t("CreateNewTenderPhase")}
                icon={
                  <i
                    className="pi pi-plus-circle iconTable"
                    style={{ color: "var(--color-red)" }}
                  ></i>
                }
                onClick={() => setShowCreatePhaseConfirm(true)}
                label={t("CreateNewTenderPhase")}
              />
            )}

            <MButton
              tooltip={t("FinishTender")}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "var(--color-red)" }}
                ></i>
              }
              onClick={handleFinish}
              label={t("FinishTender")}
            />
          </>
        }
      />

      {/* Confirm Finish */}
      <MMessageBoard
        title={t("ConfirmFinishTender")}
        open={showFinishConfirm}
        onClose={() => setShowFinishConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {t("AreYouSureYouWantToFinishTender")}
          </div>
        }
        footer={
          <>
            <MButton
              label={t("Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                ></i>
              }
              onClick={() => setShowFinishConfirm(false)}
            />
            <MButton
              label={t("Confirm")}
              onClick={confirmFinishTender}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                ></i>
              }
            />
          </>
        }
      />

      {/* Confirm New Phase */}
      <MMessageBoard
        title={t("ConfirmCreateNewPhase")}
        open={showCreatePhaseConfirm}
        onClose={() => setShowCreatePhaseConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {t("AreYouSureYouWantToCreateNewPhase")}
          </div>
        }
        footer={
          <>
            <MButton
              label={t("Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                ></i>
              }
              onClick={() => setShowCreatePhaseConfirm(false)}
            />
            <MButton
              label={t("Confirm")}
              onClick={confirmCreatePhase}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                ></i>
              }
            />
          </>
        }
      />

      {/* Main content (inline views) */}
      <div className="tiq-page-root">
        {/* =========================
            In-Quotation View (INLINE)
            ========================= */}
        {activeView === "inQuotation" && (
          <div className="firstDivContainer tiq-main-view">
            <div className="tiq-form-card">
              <InQuotationForm
                tenderInfo={hvTender}
                phases={hvPhases}
                setTenderInfo={() => { }}
                isSpotTender={isSpotTender}
                selectedPhaseId={selectedPhaseId}
                setSelectedPhaseId={setSelectedPhaseId}
                tenderSchemaOptions={tenderSchemaOptions}
              />
            </div>

            <div className="tiq-panels-row">
              <div className="tiq-panel-card">
                <CarrierResponsesTable
                  tenderId={hvTender?.Id}
                  phases={hvPhases}
                  responses={hvQuotesAll}
                  carriers={carriersWithQuotes}
                  isSpotTender={isSpotTender}
                  setActiveView={setActiveView}
                  setSelectedCarrierId={setSelectedCarrierId}
                />
              </div>

              <div className="tiq-panel-card">
                <TenderQuotesAnalysis
                  tenderId={hvTender?.Id}
                  routeId={hvTender?.RouteId}
                  isSpotTender={isSpotTender}
                  lanes={hvLanes}
                  laneSummaries={hvLaneSummaries}
                />

              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            Carrier Detail View (INLINE)
            ========================================== */}
        {activeView === "carrier" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {t("Carrier Responses")}
            </span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  {!isSpotTender && (
                    <MSelect
                      label={t("Phase")}
                      value={
                        hvPhases
                          .map((phase) => ({
                            label: phase.Name || `${t("Phase")} ${phase.Id}`,
                            value: phase.Id,
                          }))
                          .find((opt) => opt.value === detailPhaseId) || null
                      }
                      onChangeValue={(newValue) => {
                        if (newValue && typeof newValue === "object") {
                          handleDetailPhaseChange(newValue.value);
                        } else {
                          handleDetailPhaseChange(null);
                        }
                      }}
                      options={hvPhases.map((phase) => ({
                        label: phase.Name || `${t("Phase")} ${phase.Id}`,
                        value: phase.Id,
                      }))}
                      autocomplete
                      required
                    />
                  )}

                  <MSelect
                    label={t("Carrier")}
                    value={
                      carriersWithQuotes
                        .map((c) => ({
                          label: c.CarrierName || c.Name,
                          value: c.CarrierId || c.Id,
                        }))
                        .find(
                          (opt) => opt.value === (selectedCarrierId ?? null)
                        ) || null
                    }
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setSelectedCarrierId(newValue.value);
                      } else {
                        setSelectedCarrierId(null);
                      }
                    }}
                    options={carriersWithQuotes.map((c) => ({
                      label: c.CarrierName || c.Name,
                      value: c.CarrierId || c.Id,
                    }))}
                    autocomplete
                    required
                  />

                  <MSelect
                    label={t("BestMetric")}
                    value={{
                      label:
                        bestMetric === "bid"
                          ? t("BidValue")
                          : bestMetric === "loads"
                            ? t("Loads")
                            : t("EstimatedTransitDays"),
                      value: bestMetric,
                    }}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("bid");
                      }
                    }}
                    options={[
                      { label: t("BidValue"), value: "bid" },
                      { label: t("Loads"), value: "loads" },
                      { label: t("EstimatedTransitDays"), value: "etd" },
                    ]}
                    autocomplete
                    required
                  />
                </div>
              </div>
            </div>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <CarrierResponseDetailTable
                  tenderInfo={hvTender}
                  selectedCarrier={selectedCarrierId}
                  selectedPhaseId={isSpotTender ? null : detailPhaseId}
                  quotes={detailQuotes}
                  lanes={hvLanes} // IMPORTANT: keep current lanes here
                  isSpotTender={isSpotTender}
                  bestMetric={bestMetric}
                  laneBestMap={laneBestMap}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                  onRefresh={() =>
                    refreshCarrierDetailTable(
                      selectedCarrierId,
                      isSpotTender ? null : detailPhaseId
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            Lane View (INLINE)
            ========================================== */}
        {activeView === "lane" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">{t("Lane")}</span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  <MSelect
                    label={t("BestMetric")}
                    value={{
                      label:
                        bestMetric === "bid"
                          ? t("BidValue")
                          : bestMetric === "loads"
                            ? t("Loads")
                            : t("EstimatedTransitDays"),
                      value: bestMetric,
                    }}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("bid");
                      }
                    }}
                    options={[
                      { label: t("BidValue"), value: "bid" },
                      { label: t("Loads"), value: "loads" },
                      { label: t("EstimatedTransitDays"), value: "etd" },
                    ]}
                    autocomplete
                    required
                  />
                </div>
              </div>
            </div>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <ViewLaneTable
                  tenderInfo={hvTender}
                  acceptedQuotes={hvQuotesAcceptedEnriched}
                  allQuotes={hvQuotesAllEnriched}
                  lanes={hvAllLanes} // IMPORTANT: use ALL lanes here
                  carriers={carriersWithQuotes}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  metricKey={bestMetric === "etd" ? "transit" : bestMetric}
                  isFinished={isTenderFinished}
                  isSpotTender={isSpotTender}
                  // IMPORTANT: pass the REAL last phase id (not the selected phase)
                  currentPhaseId={lastPhaseId}
                  laneBestMap={laneBestMap}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                />
              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            Summary View (INLINE)
            ========================================== */}
        {activeView === "summary" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {t("Summary") || "Summary"}
            </span>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <TenderSummaryTable
                  tenderInfo={hvTender}
                  lanes={hvAllLanes} // IMPORTANT: use ALL lanes here
                  phases={hvPhases}
                  isSpotTender={isSpotTender}
                  rows={summaryLaneRows}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

TenderInQuotationPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};